import clsx from "clsx";

import { QuestionnaireModel, SubmissionModel } from "@interfaces/index";
import { QUESTION_FILE_EXTENSIONS, formatPeriodRange } from "@utils/index";
import {
  QuestionnaireHeader,
  FormText,
  FormFile,
  FileItem,
  Button,
  ChecksSvg,
  FlagPlusSvg,
} from "@components/index";

export const TabQuestionnaire = ({
  questionnaireObj,
  questionnaireStatus,
  questionnaireAnswers,
  submissionDetails,
  isPendingQuestionnaireUpdateAnswer,
  submitQuestionnarieIsDisabled,
  onChangeAnswer,
  onSaveAnswer,
  onSelectFiles,
  onRemoveAttachment,
  onSubmitClick,
}: {
  questionnaireObj: QuestionnaireModel;
  questionnaireStatus?: QuestionnaireModel["status"];
  questionnaireAnswers: Record<string, string>;
  submissionDetails?: SubmissionModel;
  isPendingQuestionnaireUpdateAnswer: boolean;
  submitQuestionnarieIsDisabled: boolean;
  onChangeAnswer: (uniqueId: string, value: string) => void;
  onSaveAnswer: (uniqueId: string) => void;
  onSelectFiles: (target: HTMLInputElement, uniqueId: string) => void;
  onRemoveAttachment: (attachmentId: string, uniqueId: string) => void;
  onSubmitClick: () => void;
}) => (
  <div className="pt-4 md:pt-6">
    <QuestionnaireHeader
      name={questionnaireObj.name}
      status={questionnaireObj.status}
      statusLabel={questionnaireObj.statusLabel}
    />
    {!questionnaireObj.questions.length ? (
      <div className="flex flex-col items-center justify-center py-10 text-center md:py-20">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-black-4 text-black-32 *:size-[32px] md:h-20 md:w-20 *:md:size-[40px]">
          <FlagPlusSvg />
        </div>
        <div className="text-400 font-bold text-black-100 md:text-500">
          Not available yet
        </div>
        <div className="mt-2 max-w-sm text-200 text-black-54 md:text-300">
          Your questionnaire for{" "}
          <strong className="text-black-70">
            {submissionDetails && formatPeriodRange(submissionDetails.period)}
          </strong>{" "}
          hasn't been launched yet. We'll notify you when it's ready to
          complete.
        </div>
      </div>
    ) : (
      <div className="flex flex-col gap-y-4 py-6">
        {[...questionnaireObj.questions]
          .sort((a, b) => a.order - b.order)
          .map(
            ({
              uniqueId,
              order,
              title,
              subtitle,
              documentNote,
              attachmentRequirement,
              attachments,
              answer,
            }) => (
              <div
                key={uniqueId}
                className="flex flex-col gap-y-4 rounded-lg border border-black-8 p-6"
              >
                <div className="flex items-start gap-x-3">
                  <div className="mt-0.5 shrink-0 rounded-lg bg-primary-104 px-2 py-0.5 text-100 font-bold text-primary-300">
                    {order + 1}
                  </div>
                  <div>
                    <div className="text-300 font-bold text-black-100">
                      {title}
                    </div>
                    {subtitle && (
                      <div className="mt-1 text-200 text-black-54">
                        {subtitle}
                      </div>
                    )}
                  </div>
                </div>
                <FormText
                  name="answer"
                  label=""
                  placeholder="Your answer"
                  disabled={
                    isPendingQuestionnaireUpdateAnswer ||
                    questionnaireStatus === "completed"
                  }
                  value={questionnaireAnswers[uniqueId] || ""}
                  onChange={({ currentTarget }) =>
                    onChangeAnswer(uniqueId, currentTarget.value)
                  }
                  onBlur={(e) => {
                    if ((e.currentTarget.value || "") !== (answer || "")) {
                      onSaveAnswer(uniqueId);
                    }
                  }}
                />
                {["optional", "required"].includes(attachmentRequirement) && (
                  <div className="flex flex-col gap-y-2">
                    {questionnaireStatus !== "completed" && (
                      <>
                        {documentNote && (
                          <div className="text-200 text-black-54">
                            {documentNote}
                          </div>
                        )}
                        <FormFile
                          name="files"
                          label="Attach supporting documents"
                          accept={QUESTION_FILE_EXTENSIONS}
                          onChange={(event) => {
                            onSelectFiles(
                              event.target as HTMLInputElement,
                              uniqueId,
                            );
                            event.preventDefault();
                          }}
                        />
                      </>
                    )}
                    <div
                      className={clsx(
                        !attachments?.length
                          ? "hidden"
                          : "flex flex-col gap-y-2",
                      )}
                    >
                      {attachments?.map((attachment) => (
                        <FileItem
                          key={attachment.uniqueId}
                          file={attachment}
                          isEditing={questionnaireStatus !== "completed"}
                          onRemove={
                            questionnaireStatus === "completed"
                              ? undefined
                              : () =>
                                  onRemoveAttachment(
                                    attachment.uniqueId,
                                    uniqueId,
                                  )
                          }
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ),
          )}
      </div>
    )}
    {!!questionnaireObj.questions.length &&
      questionnaireObj.status !== "completed" && (
        <div className="flex justify-center md:justify-end">
          <Button
            title="Submit questionnaire"
            height="small"
            disabled={submitQuestionnarieIsDisabled}
            leftIcon={<ChecksSvg />}
            className={clsx(
              "w-full bg-secondary-100 text-white-100 hover:opacity-80 md:max-w-60",
              submitQuestionnarieIsDisabled && "opacity-60 hover:opacity-60",
            )}
            onClick={onSubmitClick}
          />
        </div>
      )}
  </div>
);
